import { Editor } from '@tiptap/react';
import { JSONContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { TaskItem } from '@tiptap/extension-task-item';
import { TaskList } from '@tiptap/extension-task-list';
import { TextAlign } from '@tiptap/extension-text-align';
import { Typography } from '@tiptap/extension-typography';
import { Highlight } from '@tiptap/extension-highlight';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Underline } from '@tiptap/extension-underline';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import TiptapColor from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import { RichTextLink } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-extension/link-extension';
import { RichTextParagraphClass } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-extension/paragraph-class-extension';
import { RichTextSelection } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-extension/selection-extension';
import { RichTextTrailingNode } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-extension/trailing-node-extension';
import { jsonParse } from 'libs/end-to-end/src/utils/utility/json-parse';
import { ProsemirrorOutput } from 'libs/end-to-end/src/prosemirror';
import { z } from 'zod';

// Don't change this unless the Tiptap library changes JSONContent.
// We don't own this schema, it's purely used for parsing to the library's type.
const RichTextContent: z.ZodType<JSONContent> = z.lazy(() =>
  z
    .object({
      type: z.string().optional(),
      attrs: z.record(z.string(), z.any()).optional(),
      content: z.array(RichTextContent).optional(),
      marks: z
        .array(
          z
            .object({
              type: z.string(),
              attrs: z.record(z.string(), z.any()).optional(),
            })
            .catchall(z.any()),
        )
        .optional(),
      text: z.string().optional(),
    })
    .catchall(z.any()),
);

export type RichTextContent = z.infer<typeof RichTextContent>;

export const deserializeRichTextContent = (
  content: string,
): RichTextContent | null => {
  const result = jsonParse(content, RichTextContent);
  if (result.resultType === 'err') {
    return null;
  }

  return result.value;
};

export const createRichTextEditorExtensions = () => {
  return [
    StarterKit,
    Placeholder.configure({
      placeholder: 'Write something...',
    }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Underline,
    TaskList,
    TaskItem.configure({ nested: true }),
    Highlight.configure({ multicolor: true }),
    Typography,
    Superscript,
    Subscript,
    RichTextSelection,
    RichTextTrailingNode,
    RichTextLink.configure({ openOnClick: false }),
    RichTextParagraphClass,
    TextStyle,
    TiptapColor.configure({ types: ['textStyle'] }),
    FontFamily.configure({ types: ['textStyle'] }),
  ];
};

const createRichTextPlainTextExtensions = () => {
  return [
    StarterKit,
    Underline,
    RichTextLink.configure({ openOnClick: false }),
    RichTextParagraphClass,
    TextStyle,
    TiptapColor.configure({ types: ['textStyle'] }),
  ];
};

export const getPlainTextFromProsemirror = (content: ProsemirrorOutput) => {
  const editor = new Editor({
    extensions: createRichTextPlainTextExtensions(),
    content,
    editable: false,
  });

  try {
    return editor.getText().trim();
  } finally {
    editor.destroy();
  }
};
